import type { GetChildCount } from "ag-grid-enterprise";
import type { HardgoodsInventoryRow } from "~produce/inventory/hardgoods/types";

import { type CycleCountAssignmentStatus } from "~/gql/graphqlBase.generated";

export const HARDGOODS_GRID_ID = "hardgoods";

export const HARDGOODS_ITEM_QUERY_PARAM = "itemName";

const NO_BIN_PLACEHOLDER = "—";
const GROUP_ROW_ID_PREFIX = "group";

export const HARDGOODS_GROUP_FIELDS = ["itemName"] as const;

export type HardgoodsSsrmRow = {
	rowId: string | null;
	materialItemId: string;
	itemName: string;
	sku: string;
	category: string;
	baseUnit?: string | null;
	batchTracked: boolean;
	isOrganic: boolean;
	siteLocationId: string | null;
	siteName: string;
	locationId: string;
	locationName: string;
	onHand: number;
	required: number | null;
	netShort?: number | null;
	requiredEachesPerContainer?: number | null;
	requiredJobContainerCount?: number;
	requiredUnitLabel?: string | null;
	assignmentStatus: CycleCountAssignmentStatus | null;
	activeAssignmentId: string | null;
	lastCountedAt: string | null;
	childCount: number;
	isNeedOnly?: boolean;
};

export interface HardgoodsRowIdParams {
	data?: HardgoodsSsrmRow;
	level?: number;
	parentKeys?: readonly string[];
}

const readHardgoodsGroupValue = (data: HardgoodsSsrmRow | undefined, level: number): string => {
	const groupField = HARDGOODS_GROUP_FIELDS[level];
	const groupValue = groupField ? (data as Record<string, unknown> | undefined)?.[groupField] : undefined;
	return String(groupValue ?? "");
};

const buildHardgoodsGroupRowId = ({ data, level = 0, parentKeys }: HardgoodsRowIdParams): string =>
	[GROUP_ROW_ID_PREFIX, ...(parentKeys ?? []), readHardgoodsGroupValue(data, level)].join("-");

export const computeHardgoodsRowId = (params: HardgoodsRowIdParams): string =>
	params.data?.rowId ?? buildHardgoodsGroupRowId(params);

export const computeHardgoodsSelectionRowId = (row: HardgoodsSsrmRow): string => computeHardgoodsRowId({ data: row });

export const getHardgoodsChildCount: GetChildCount = (data) => data?.childCount;

export const isHardgoodsLeafRow = (row: Pick<HardgoodsSsrmRow, "rowId">): boolean => row.rowId != null;

export const toHardgoodsInventoryRow = (row: HardgoodsSsrmRow): HardgoodsInventoryRow => ({
	activeAssignmentId: row.activeAssignmentId,
	assignmentStatus: row.assignmentStatus,
	baseUnit: row.baseUnit ?? undefined,
	batchTracked: row.batchTracked,
	category: row.category,
	id: computeHardgoodsSelectionRowId(row),
	isOrganic: row.isOrganic,
	itemName: row.itemName,
	lastCountedAt: row.lastCountedAt ?? undefined,
	locationId: row.locationId || NO_BIN_PLACEHOLDER,
	locationName: row.locationId ? row.locationName : NO_BIN_PLACEHOLDER,
	materialItemId: row.materialItemId,
	onHand: row.onHand,
	required: row.required,
	siteLocationId: row.siteLocationId ?? undefined,
	siteName: row.siteName,
	sku: row.sku
});
